/**
 * envelopeResolve.js — Single source of truth for the Building → Floor → Zone
 * envelope inheritance cascade (exterior walls + fenestration).
 *
 * The 3D Construct screen lets a user define exterior-wall construction and
 * fenestration at three levels. Each level stores ONLY what is explicitly set
 * there; a wall inherits from its parent when a level is unset:
 *
 *     zone wall override  →  floor orientation  →  building orientation default
 *                         →  legacy building openings (orientationOpenings)
 *                         →  building single fallback (envelope_defaults.wall_construction_id)
 *
 * Storage (see projectStore.js):
 *   • building: envelope_defaults.orientationData[dir] = { walls, fenestration }
 *   • floor:    floor.orientationData[dir]             = { walls, fenestration }
 *   • zone:     zone.wallOverrides[wallId]             = { walls, fenestration }
 *   • legacy:   building.orientationOpenings[dir]      = a different, camelCase shape,
 *               normalised by normalizeLegacyOpenings below. No longer authored (the
 *               WallPropertiesPanel that wrote it was removed) but present in saved files.
 *
 * Wall identity is the stable per-edge id `${zoneId}_wall_${edgeIndex}` produced
 * by footprintToSurfaces (geometry.js). Direction `dir` is the bin8 compass bucket
 * of the wall's true azimuth.
 *
 * Resolution semantics:
 *   • Walls use MEANINGFUL-VALUE semantics — a level "provides" a construction only
 *     when it has a construction_id / custom_u / custom_layers. (There is no concept
 *     of "no wall construction"; a wall always needs one, so this matches the old
 *     behaviour and avoids the empty-object trap.)
 *   • Fenestration uses KEY-PRESENCE semantics — a level "provides" fenestration when
 *     the `fenestration` key (or the legacy `windows`/`doors` keys) is present, even
 *     if it resolves to "no openings". This lets a zone explicitly override an
 *     inherited window with "none". "Reset to inherited" deletes the key entirely.
 *
 * EVERY consumer (idfGenerator, retvEngine, epfEngine, envelopeCompleteness,
 * Scene3D, EnvelopeDataPanel) resolves through this module so the 3D render, the
 * live RETV/EPF badges, the completeness checker and the exported IDF can never
 * disagree.
 */

// ── Fenestration normalisation (lifted from idfGenerator so all callers agree) ──

/**
 * Normalise a stored level object's fenestration into a stable shape:
 *   { windows: [...0..5], ventilators: obj|null, doors: obj|null }
 * Handles the current nested model, the old single-type model, and the very old
 * top-level `windows`/`doors` keys.
 */
export function resolveOrientationFenestration(od) {
  const winArray = (w) => (Array.isArray(w) ? w.filter(Boolean) : (w ? [w] : []));
  const fen = od?.fenestration;
  if (fen) {
    if (fen.windows !== undefined || fen.ventilators !== undefined || fen.doors !== undefined) {
      return { windows: winArray(fen.windows), ventilators: fen.ventilators || null, doors: fen.doors || null };
    }
    if (fen.type === 'window')     return { windows: [fen], ventilators: null, doors: null };
    if (fen.type === 'ventilator') return { windows: [],    ventilators: fen,  doors: null };
    if (fen.type === 'door')       return { windows: [],    ventilators: null, doors: fen  };
  }
  if (od?.windows) return { windows: winArray(od.windows), ventilators: null, doors: od.doors || null };
  return { windows: [], ventilators: null, doors: null };
}

// ── Level predicates ────────────────────────────────────────────────────────

/** A level provides a wall construction when it carries a meaningful value. */
export function levelHasWalls(w) {
  return !!(w && (w.construction_id
    || w.custom_u != null
    || (Array.isArray(w.custom_layers) && w.custom_layers.length)));
}

/** A level provides fenestration when the key is present (explicit, may be "none"). */
export function levelHasFen(od) {
  if (!od) return false;
  return Object.prototype.hasOwnProperty.call(od, 'fenestration')
    || Object.prototype.hasOwnProperty.call(od, 'windows')   // legacy
    || Object.prototype.hasOwnProperty.call(od, 'doors');    // legacy
}

// ── Legacy building-level openings (building.orientationOpenings) ────────────
//
// `orientationOpenings[dir]` is the container the REMOVED WallPropertiesPanel wrote
// ("click a wall, assign the whole orientation"). Nothing authors into it any more -
// Scene3D.jsx records its removal and EnvelopeDataPanel writes the three modern levels -
// but saved projects still carry it, and until 20 Sep 2026 only retvEngine and epfEngine
// read it. The IDF, the 3D scene, the daylight gate, the baselines and LCA all resolve
// through this module and so saw NOTHING, meaning a legacy project showed windows in the
// RETV/EPF badges that existed in no exported model. Normalising it here, as one cascade
// level, is what puts every consumer back on the same data.
//
// Two shape families live in here, neither matching the modern one:
//   • type-reference assignments - { window_assignments: [{ typeId, wwr }], ... } where
//     wwr is a FRACTION (the old code multiplied it by grossArea directly), against the
//     modern `wwr` which is a PERCENT;
//   • flat entries - { windows: [{ glazingId, width, height, count, overhang, finLeft,
//     finRight }], doors: [...], ventilators: [...] } in camelCase.
// Both are converted to the modern snake_case window shape so exactly one vocabulary
// reaches the engines.

const ZERO = { depth: 0, gap: 0 };
const proj = (p) => (p && (p.depth || 0) > 0 ? { depth: p.depth, gap: p.gap || 0 } : { ...ZERO });

/**
 * Legacy entries stored shading DEPTHS but never the `shading_type` flag that
 * resolveActiveShading masks on, so a naive conversion would silently delete every legacy
 * overhang and fin. Synthesise the flag from which depths are actually set.
 * "Both fins, no overhang" has no name in the vocabulary, so it is expressed as
 * 'overhang_fins' with a zero-depth overhang - the mask then yields exactly the two fins.
 */
function legacyShadingType(ovh, finL, finR) {
  const o = ovh.depth > 0, l = finL.depth > 0, r = finR.depth > 0;
  if (o && l && r) return 'overhang_fins';
  if (o && l) return 'overhang_fin_left';
  if (o && r) return 'overhang_fin_right';
  if (l && r) return 'overhang_fins';
  if (o) return 'overhang';
  if (l) return 'fin_left';
  if (r) return 'fin_right';
  return 'none';
}

/** Shared tail: shading + glazing identity, for both legacy window flavours. */
function legacyGlazingFields(src) {
  const overhang = proj(src.overhang);
  const fin_left = proj(src.fin_left || src.finLeft);
  const fin_right = proj(src.fin_right || src.finRight);
  return {
    glazing_id: src.glazing_id || src.glazingId || 'single-clear',
    custom_u: src.custom_u ?? src.customGlazingU ?? null,
    custom_shgc: src.custom_shgc ?? src.customSHGC ?? null,
    custom_vlt: src.custom_vlt ?? null,
    glazing_area_pct: src.glazing_area_pct ?? 80,
    frame_material: src.frame_material || 'alum-tb',
    overhang, fin_left, fin_right,
    shading_type: src.shading_type || legacyShadingType(overhang, fin_left, fin_right),
  };
}

/** { typeId, wwr(fraction) } + the type library → one modern WWR-driven window. */
function fromAssignment(a, types) {
  const t = (types || []).find((x) => x.id === a?.typeId);
  if (!t || !(a.wwr > 0)) return null;
  return {
    method: 'wwr',
    wwr: a.wwr * 100, // fraction → percent (the modern unit)
    width: t.width || 1.2,
    height: t.height || 1.5,
    ...legacyGlazingFields(t),
  };
}

/** A flat camelCase entry → one modern count-driven (or WWR-driven) window. */
function fromFlat(w) {
  if (!w) return null;
  const count = w.count || 0;
  const wwr = w.wwr != null ? (w.wwr > 1 ? w.wwr : w.wwr * 100) : null;
  if (!count && !(wwr > 0)) return null;
  return {
    method: count > 0 ? 'dimensions' : 'wwr',
    wwr: wwr ?? 0,
    count,
    width: w.width || 1.2,
    height: w.height || 1.5,
    ...legacyGlazingFields(w),
  };
}

/**
 * Legacy ventilators/doors are ARRAYS, but the modern `fen` carries one object each
 * (see idfGenerator.buildSlots). Collapse to a single entry, summing the counts. A facade
 * with differently-sized legacy doors therefore reports the total count at the first
 * entry's dimensions - lossy, but every alternative either drops openings outright or
 * feeds the generator a shape it would emit as garbage geometry.
 */
function collapse(list) {
  const items = (Array.isArray(list) ? list : [list]).filter(Boolean);
  if (!items.length) return null;
  const total = items.reduce((s, x) => s + (x.count || 0), 0);
  return { ...items[0], count: total || items[0].count || 0 };
}

/** The legacy container for one direction as a modern { walls, fenestration } level. */
function buildLegacyOd(building, dir) {
  const stored = (building?.orientationOpenings || {})[dir];
  if (!stored || typeof stored !== 'object') return null;

  const od = {};
  if (stored.constructionId) od.walls = { construction_id: stored.constructionId };

  // Already the modern shape (some projects and tests store it here) → pass straight through.
  if (stored.fenestration) {
    od.fenestration = stored.fenestration;
    return od;
  }

  const wTypes = building.window_types || [];
  const vTypes = building.ventilator_types || [];
  const dTypes = building.door_types || [];

  let windows = (stored.window_assignments || []).map((a) => fromAssignment(a, wTypes)).filter(Boolean);
  let ventSrc = (stored.vent_assignments || []).map((a) => fromAssignment(a, vTypes)).filter(Boolean);
  let doorSrc = (stored.door_assignments || [])
    .map((a) => {
      const t = dTypes.find((x) => x.id === a.typeId);
      return t && (a.count || 0) > 0
        ? { count: a.count, width: t.width || 0.9, height: t.height || 2.1, u_value: t.uValue ?? 2.0, material: t.material }
        : null;
    })
    .filter(Boolean);

  if (!windows.length) windows = (Array.isArray(stored.windows) ? stored.windows : [stored.windows]).map(fromFlat).filter(Boolean);
  if (!ventSrc.length) {
    const v = collapse(stored.ventilators);
    const vm = v ? fromFlat({ width: 0.6, height: 0.6, ...v }) : null;
    ventSrc = vm ? [vm] : [];
  }
  if (!doorSrc.length) {
    const d = collapse(stored.doors);
    if (d && (d.count || 0) > 0) {
      doorSrc = [{
        count: d.count, width: d.width || 0.9, height: d.height || 2.1,
        u_value: d.u_value ?? d.uValue ?? 2.0,
        material: d.material,
        glazed_area_pct: d.glazed_area_pct ?? 0,
        glazing_id: d.glazing_id || d.glazingId,
        custom_shgc: d.custom_shgc ?? null,
      }];
    }
  }

  // A bare `wwr` on the container (no explicit windows) still means glazing.
  if (!windows.length && stored.wwr != null) {
    const ed = building.envelope_defaults || {};
    const one = fromFlat({
      wwr: stored.wwr,
      width: ed.window_width, height: ed.window_height,
      glazingId: stored.glazingId ?? ed.window_glazing_id,
      overhang: ed.overhang, finLeft: ed.fin_left, finRight: ed.fin_right,
    });
    if (one) windows = [one];
  }

  if (windows.length || ventSrc.length || doorSrc.length) {
    od.fenestration = { windows, ventilators: ventSrc[0] || null, doors: doorSrc[0] || null };
  }
  return (od.walls || od.fenestration) ? od : null;
}

/**
 * Derived fresh on every call, deliberately NOT memoised.
 *
 * A cache keyed on the building object looks safe (zustand replaces the building on every
 * edit) but is not: callers mutate a building IN PLACE and re-read it within one pass.
 * transformGeometry is the proof - it measures facadeWWR, caps the windows, then measures
 * again on the same object, and a cached normalisation hands back the pre-cap glazing so
 * the 40% cap silently does nothing. genopt's applyCombo variants and ECM edits mutate the
 * same way. The work here is trivial and only happens when this direction actually carries
 * legacy data, so modern projects pay one property lookup per wall.
 */
export function normalizeLegacyOpenings(building, dir) {
  const stored = building?.orientationOpenings?.[dir];
  if (!stored || typeof stored !== 'object') return null;
  try { return buildLegacyOd(building, dir); } catch { return null; }
}

/**
 * A building-wide `envelope_defaults.wwr` as a synthesised fenestration level.
 *
 * No UI writes this field - blankEnvelopeDefaults sets it to null and nothing since assigns it -
 * but saved projects carry it, and RETV honoured it through the private reader that used to live
 * in retvEngine. Deleting that reader would have silently dropped those windows, so it is folded
 * in here instead, as the LOWEST-priority fenestration source. The gain is that such a project now
 * shows the same glazing in the IDF, the 3D scene and the daylight gate that RETV always claimed.
 * Historically stored as a fraction; values > 1 are read as an already-percent figure.
 */
function defaultWwrFen(building) {
  const ed = building?.envelope_defaults || {};
  const raw = Number(ed.wwr);
  if (!(raw > 0)) return null;
  return {
    fenestration: {
      windows: [{
        method: 'wwr',
        wwr: raw > 1 ? raw : raw * 100,
        width: ed.window_width || 1.2,
        height: ed.window_height || 1.5,
        ...legacyGlazingFields({
          glazingId: ed.window_glazing_id,
          overhang: ed.overhang, finLeft: ed.fin_left, finRight: ed.fin_right,
        }),
      }],
      ventilators: null,
      doors: null,
    },
  };
}

// ── Core cascade resolver ─────────────────────────────────────────────────────

/**
 * Resolve the effective envelope for one exterior wall.
 *
 * @param {Object}  building
 * @param {Object|null} floor  the wall's floor (may be null for building-level previews)
 * @param {Object|null} zone   the wall's zone  (may be null when no zone context)
 * @param {string}  wallId     stable wall id `${zoneId}_wall_${edge}`
 * @param {string}  dir        compass bucket (bin8 of true azimuth)
 * @returns {{
 *   od: Object,            // merged level-shape { walls, fenestration?/windows?/doors? } for engines
 *   walls: Object|null,    // resolved walls object (or null)
 *   fen: Object,           // normalised { windows:[], ventilators, doors }
 *   wallSource: 'zone'|'floor'|'building'|'legacy'|'fallback'|'none',
 *   fenSource:  'zone'|'floor'|'building'|'legacy'|'default-wwr'|'none',
 *   wallKey: string,       // stable identity of the resolved wall construction
 *   fenKey:  string,       // stable LRM/group identity of the resolved fenestration
 *   retvGroupKey: string,  // `${wallKey}|${fenKey}` — physics group identity
 *   hasNewModel: boolean,  // true when any explicit (non-fallback) data was found
 * }}
 */
export function resolveWallEnvelope(building, floor, zone, wallId, dir) {
  const ed      = building?.envelope_defaults || {};
  const zoneOd  = zone  ? ((zone.wallOverrides || {})[wallId]) : null;
  const floorOd = floor ? ((floor.orientationData || {})[dir]) : null;
  const bldgOd  = (ed.orientationData || {})[dir] || null;
  // Legacy building-level container, normalised to the modern level shape. It sits BELOW
  // the three authored levels (anything the user has since set wins) and ABOVE the single
  // fallback - the same precedence retvEngine expressed with its `hasNewModel` test.
  const legacyOd = normalizeLegacyOpenings(building, dir);
  const floorId = floor?.id;

  // ── Walls (meaningful-value semantics) ──
  let walls = null, wallSource = 'none', wallKey = 'n';
  if (levelHasWalls(zoneOd?.walls)) {
    walls = zoneOd.walls; wallSource = 'zone'; wallKey = `z:${zone.id}:${wallId}`;
  } else if (levelHasWalls(floorOd?.walls)) {
    walls = floorOd.walls; wallSource = 'floor'; wallKey = `f:${floorId}:${dir}`;
  } else if (levelHasWalls(bldgOd?.walls)) {
    walls = bldgOd.walls; wallSource = 'building'; wallKey = `b:${dir}`;
  } else if (levelHasWalls(legacyOd?.walls)) {
    walls = legacyOd.walls; wallSource = 'legacy'; wallKey = `L:${dir}`;
  } else if (ed.wall_construction_id) {
    walls = { construction_id: ed.wall_construction_id }; wallSource = 'fallback'; wallKey = 'fb';
  }

  // ── Fenestration (key-presence semantics) ──
  let fenOd = null, fenSource = 'none';
  if (zoneOd && levelHasFen(zoneOd))       { fenOd = zoneOd;  fenSource = 'zone'; }
  else if (floorOd && levelHasFen(floorOd)) { fenOd = floorOd; fenSource = 'floor'; }
  else if (bldgOd && levelHasFen(bldgOd))   { fenOd = bldgOd;  fenSource = 'building'; }
  else if (legacyOd && levelHasFen(legacyOd)) { fenOd = legacyOd; fenSource = 'legacy'; }
  else {
    // Nothing anywhere declared fenestration — fall back to a building-wide default WWR if the
    // project carries one. See defaultWwrFen: this keeps a legacy field working everywhere at
    // once rather than in RETV alone.
    const dw = defaultWwrFen(building);
    if (dw) { fenOd = dw; fenSource = 'default-wwr'; }
  }

  // Group key — keeps Largest-Remainder count distribution per (floor, dir) for
  // inherited walls (matching the legacy stamped behaviour) and isolates per-wall
  // zone overrides so "This wall only / count N" means exactly N panes on that wall.
  const fenKey = fenSource === 'zone'     ? `z:${zone.id}:${wallId}`
               : fenSource === 'floor'    ? `f:${floorId}:${dir}`
               : fenSource === 'building' ? `b:${floorId}:${dir}`
               : fenSource === 'legacy'   ? `L:${floorId}:${dir}`
               : fenSource === 'default-wwr' ? `d:${floorId}:${dir}`
               :                            `n:${floorId}:${dir}`;

  // Merged level-shape object the engines' resolveFromFloorData / the IDF's
  // resolveOrientationFenestration can consume directly.
  const od = { walls: walls || {} };
  if (fenOd) {
    if (Object.prototype.hasOwnProperty.call(fenOd, 'fenestration')) od.fenestration = fenOd.fenestration;
    if (Object.prototype.hasOwnProperty.call(fenOd, 'windows'))      od.windows = fenOd.windows; // legacy
    if (Object.prototype.hasOwnProperty.call(fenOd, 'doors'))        od.doors = fenOd.doors;     // legacy
  }

  const hasNewModel = wallSource === 'zone' || wallSource === 'floor' || wallSource === 'building'
    || wallSource === 'legacy' || fenSource !== 'none';

  return {
    od,
    walls,
    fen: resolveOrientationFenestration(od),
    wallSource, fenSource,
    wallKey, fenKey,
    retvGroupKey: `${wallKey}|${fenKey}`,
    hasNewModel,
  };
}

/** Resolved exterior-wall construction id for a wall (null when unset everywhere). */
export function resolveWallConstructionId(building, floor, zone, wallId, dir) {
  return resolveWallEnvelope(building, floor, zone, wallId, dir).walls?.construction_id || null;
}

/** Normalised fenestration ({windows[],ventilators,doors}) for a wall. */
export function resolveWallFen(building, floor, zone, wallId, dir) {
  return resolveWallEnvelope(building, floor, zone, wallId, dir).fen;
}

// ── External-wall enumeration (shared so every consumer sees the same walls) ──

/**
 * Yield every EXTERIOR wall in the building with its resolution context.
 * Partitions (detected per floor) are excluded. Requires the geometry helpers to
 * be passed in by the caller to avoid a hard import cycle is unnecessary — we import
 * them directly here since geometry.js has no dependency on this module.
 */
import { footprintToSurfaces, detectPartitions, applyBuildingRotation, bin8 } from './geometry.js';

/**
 * @returns {Array<{ floor, zone, surf, wallId, dir, grossArea }>}
 */
export function enumerateExteriorWalls(building) {
  const north = building?.north_axis || 0;
  const out = [];
  for (const floor of (building?.floors || [])) {
    const partitions = detectPartitions(floor.zones);
    const partSet = new Set();
    partitions.forEach((p) => { partSet.add(p.wall1); partSet.add(p.wall2); });
    for (const zone of floor.zones) {
      const surf = footprintToSurfaces(zone.footprint, floor.elevation, floor.height, zone.id, zone.name);
      for (const wall of surf.walls) {
        if (partSet.has(wall.id)) continue;
        const dir = bin8(applyBuildingRotation(wall.azimuth, north));
        const v = wall.vertices;
        const dx = v[1].x - v[0].x, dy = v[1].y - v[0].y;
        const grossArea = Math.sqrt(dx * dx + dy * dy) * Math.abs(v[2].z - v[0].z);
        out.push({ floor, zone, surf: wall, wallId: wall.id, dir, grossArea });
      }
    }
  }
  return out;
}

/**
 * Visit EVERY stored envelope container in the cascade, at ALL FOUR levels.
 *
 * Anything that rewrites the envelope in bulk - the baseline transforms above all - must go
 * through this instead of hand-rolling the walk. Four separate three-level copies of that loop
 * existed until 20 Sep 2026 and every one of them silently skipped `zone.wallOverrides`, so a
 * wall or window drawn on an individual zone kept its PROPOSED construction, U-value, SHGC and
 * shading right through into the baseline it was being scored against.
 *
 * @param {Object} building
 * @param {(od: Object, dir: string|null, ctx: string) => void} fn
 *   `dir` is the compass bucket the container applies to, or null when it cannot be determined
 *   (a zone override on a wall the geometry can no longer enumerate). A caller whose
 *   substitution is direction-DEPENDENT must handle null; one whose substitution is uniform can
 *   ignore `dir` and will then reach every container, enumerable or not.
 */
export function eachEnvelopeContainer(building, fn) {
  if (!building) return;
  const ed = building.envelope_defaults || {};
  for (const [dir, od] of Object.entries(ed.orientationData || {})) {
    if (od) fn(od, dir, `Building default ${dir}`);
  }
  for (const f of (building.floors || [])) {
    for (const [dir, od] of Object.entries(f.orientationData || {})) {
      if (od) fn(od, dir, `${f.name || f.id} ${dir}`);
    }
  }
  for (const [dir, od] of Object.entries(building.orientationOpenings || {})) {
    if (od) fn(od, dir, `Building ${dir}`);
  }

  // Zone level. Overrides are keyed by wallId, NOT by direction, so the direction comes from the
  // wall enumeration - the same one every engine measures with. A wall the enumeration cannot
  // see is still visited, with a null dir, so a uniform substitution reaches it too.
  const dirOfWall = new Map();
  try {
    for (const { zone, wallId, dir } of (enumerateExteriorWalls(building) || [])) {
      dirOfWall.set(`${zone?.id}|${wallId}`, dir);
    }
  } catch { /* geometry too incomplete to enumerate - every zone dir stays null */ }

  const seen = new Set();
  for (const f of (building.floors || [])) {
    for (const z of (f.zones || [])) {
      for (const [wallId, od] of Object.entries(z.wallOverrides || {})) {
        // One visit per container object: some substitutions (the WWR shrink) are
        // multiplicative, so a second visit would compound them.
        if (!od || seen.has(od)) continue;
        seen.add(od);
        fn(od, dirOfWall.get(`${z.id}|${wallId}`) ?? null, `${z.name || z.id} ${wallId}`);
      }
    }
  }
}

/** Union of external-wall orientations across the whole building (for the Building node). */
export function getBuildingOrientations(building) {
  const north = building?.north_axis || 0;
  const dirs = new Set();
  for (const floor of (building?.floors || [])) {
    const partitions = detectPartitions(floor.zones);
    const partSet = new Set();
    partitions.forEach((p) => { partSet.add(p.wall1); partSet.add(p.wall2); });
    for (const zone of floor.zones) {
      const surf = footprintToSurfaces(zone.footprint, floor.elevation, floor.height, zone.id, zone.name);
      for (const wall of surf.walls) {
        if (partSet.has(wall.id)) continue;
        dirs.add(bin8(applyBuildingRotation(wall.azimuth, north)));
      }
    }
  }
  return dirs;
}
